// labor (catalog-outline.md "labor"): the timecard aggregate of @radish/core/labor on the wire (stream
// `timecard:<id>`), PIN identity for the time clock, and the timecard queries. Event payloads decode to exactly core's
// `TimecardEvent` shapes (minus `type`).
//
// Time on the wire (bugs B7/B8, T6 FIX). Facts carry instants (EpochMs), which only core and the server read. Every
// time a person sees or types is the location's wall clock: outputs are `LocationTime` objects ({ date, time, zone }),
// never an ISO instant a browser would turn into its own zone, and inputs are a date plus wall times the server reads
// in the location's zone. No field here is a string `new Date()` can parse into an instant.
import { z } from 'zod';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import { defineError } from '../../define/errors.ts';
import { defineEvent } from '../../define/event.ts';
import { additive } from '../../define/upcast.ts';
import { CloudAuthoritative, ConfigFieldsWritten } from '../config/index.ts';
import { secret } from '../../define/secret.ts';
import { Hlc } from '../../sync/protocol.ts';
import { BreakId, BreakPolicyId, DeviceId, EmployeeId, EpochMs, LocalDate, LocationId, Money, ScheduleTemplateId, ShiftId, TimecardId } from '../../primitives/codecs.ts';

// ---- shared shapes ---------------------------------------------------------------------------------------------------
const PunchSource = z.enum(['register', 'handheld', 'crew', 'crew_manager', 'backoffice', 'node', 'import']).meta({ id: 'PunchSource' });
const BreakKind = z.enum(['paid', 'unpaid', 'excess']).meta({ id: 'BreakKind' });

/** Who did it, as core records it on a correction (the envelope carries the same actor). */
export const ActorRef = z
  .discriminatedUnion('kind', [
    z.object({ kind: z.literal('staff'), employeeId: EmployeeId }),
    z.object({ kind: z.literal('user'), userId: z.uuid() }),
    z.object({ kind: z.literal('device'), deviceId: DeviceId }),
    z.object({ kind: z.literal('system'), job: z.string().max(64) }),
  ])
  .meta({ id: 'LaborActor' });

const BreakPolicySnapshot = z
  .object({
    breakPolicyId: BreakPolicyId,
    name: z.string().max(255),
    minutes: z.int().min(1).max(600),
    bufferMinutes: z.int().min(0).max(600),
    paid: z.boolean(),
    convertOverrun: z.boolean(),
    payForBuffer: z.boolean(),
  })
  .meta({ id: 'BreakPolicySnapshot' });

const TimecardBreakFact = z.object({
  breakId: BreakId,
  kind: BreakKind,
  policy: BreakPolicySnapshot.optional(),
  overrunOf: BreakId.optional(),
  startedAt: EpochMs,
  endedAt: EpochMs.optional(),
  /**
   * v2 of TimecardCreated and TimecardAdjusted (B75): legacy's own `employee_break_takens.pay_for_buffer` on an imported
   * paid break, which a back-office adjust in 'legacy' mode reads for the break's paid allowance. Absent on Next's own
   * breaks and on every v1 fact.
   */
  legacyRowPaysBuffer: z.boolean().optional(),
});

/** A card's times as a correction records them (instants, for core and the server). */
const CardTimes = z
  .object({ businessDate: LocalDate, clockedInAt: EpochMs, clockedOutAt: EpochMs.optional(), breaks: z.array(TimecardBreakFact) })
  .meta({ id: 'CardTimes' });

/** A time on the location's clock: what a person reads. Never an instant, so no client can shift it into its own zone. */
export const LocationTime = z
  .object({
    date: LocalDate,
    time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d:[0-5]\d$/),
    /** The location's IANA zone, for display only: the date and time are already on its clock. */
    zone: z.string(),
  })
  .meta({ id: 'LocationTime', description: "A time on the location's wall clock (date, HH:mm:ss, zone). Display as is; never convert." });

/** A wall time a person types, read on the location's clock: 'HH:mm' or 'HH:mm:ss'. */
const WallTime = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/)
  .meta({ id: 'WallTime', description: "A time of day typed on the location's clock, 'HH:mm' or 'HH:mm:ss'." });

/**
 * A card as a manager types it (legacy Back Office form): the clock-in date and wall times on the location's clock.
 * A clock-out or break time earlier than the clock-in is the next day (T7 FIX for breaks).
 */
const CardEdit = z
  .object({
    clockInDate: LocalDate,
    clockInTime: WallTime,
    clockOutTime: WallTime.optional(),
    breaks: z.array(z.object({ breakId: BreakId, breakPolicyId: BreakPolicyId.optional(), startTime: WallTime, endTime: WallTime.optional() })).max(20),
  })
  .meta({ id: 'CardEdit' });

const BreakView = z
  .object({
    breakId: BreakId,
    kind: BreakKind,
    name: z.string().nullable(),
    started: LocationTime,
    ended: LocationTime.nullable(),
    /**
     * labor.getTimecard only: the overstay line under the break ("Employee exceeded paid break by 12 minutes"; legacy
     * TimecardItemResource `excess_message`, core `breakOverstayLine`). Absent when there is none.
     */
    overstay: z.string().optional(),
  })
  .meta({ id: 'TimecardBreakView' });

const CardView = z
  .object({ businessDate: LocalDate, clockedIn: LocationTime, clockedOut: LocationTime.nullable(), breaks: z.array(BreakView) })
  .meta({ id: 'TimecardCardView' });

const TimecardFields = {
  timecardId: TimecardId,
  employeeId: EmployeeId,
  locationId: LocationId,
  status: z.enum(['clocked_in', 'on_break', 'clocked_out', 'auto_closed', 'deleted']),
  businessDate: LocalDate,
  clockedIn: LocationTime,
  clockedOut: LocationTime.nullable(),
  autoClosed: z.boolean(),
  breaks: z.array(BreakView),
  /** Paid hours with two decimals ("7.50"), as payroll counts them; null while the card is open. */
  hours: z.string().regex(/^\d+\.\d{2}$/).nullable(),
  version: z.int().nonnegative(),
};

export const TimecardSummary = z.object({ ...TimecardFields, editCount: z.int().nonnegative() }).meta({ id: 'TimecardSummary' });

/** One card with its full history: every punch correction with what it was, what it became, why and who (B8 FIX). */
export const TimecardSnapshot = z
  .object({
    ...TimecardFields,
    edits: z.array(
      z.object({
        kind: z.enum(['created', 'adjusted', 'deleted']),
        at: LocationTime,
        by: ActorRef,
        /** Who edited, by name (legacy Edit Log `edited_by`): a staff member's or user's name; null when unknown. Absent from an older server. */
        byName: z.string().nullable().optional(),
        reason: z.string(),
        note: z.string().nullable(),
        before: CardView.nullable(),
        after: CardView.nullable(),
      }),
    ),
  })
  .meta({ id: 'TimecardSnapshot' });

// ---- errors ----------------------------------------------------------------------------------------------------------
export const TimecardNotFound = defineError({
  code: 'labor.not_found', httpStatus: 404, message: 'errors.labor.not_found', retryable: false,
  details: z.object({ timecardId: TimecardId }),
});
export const EmployeeCannotClockIn = defineError({
  code: 'labor.employee_cannot_clock_in', httpStatus: 422, message: 'errors.labor.employee_cannot_clock_in', retryable: false,
  details: z.object({ reason: z.enum(['inactive', 'not_at_location']) }),
});
/** The card would overlap another of the employee's cards (half-open: back-to-back cards are fine, CD-L09). */
export const TimecardOverlap = defineError({
  code: 'labor.overlaps_timecard', httpStatus: 422, message: 'errors.labor.overlaps_timecard', retryable: false,
  details: z.object({ timecardId: TimecardId, clockedIn: LocationTime, clockedOut: LocationTime.nullable() }),
});
/** Legacy `timecard.spans_business_day_boundary`: a card may end at the next business-day start, not after it. */
export const SpansBusinessDay = defineError({
  code: 'labor.spans_business_day_start', httpStatus: 422, message: 'errors.labor.spans_business_day_start', retryable: false,
  details: z.object({ startsAt: LocationTime }),
});
/** Any other rule core's timecard machine refuses (`rule` is core's error code, e.g. `already_on_break`). */
export const TimecardRuleViolation = defineError({
  code: 'labor.rule_violation', httpStatus: 409, message: 'errors.labor.rule_violation', retryable: false,
  details: z.object({ rule: z.string() }),
});
export const PinNotRecognized = defineError({
  code: 'labor.pin_not_recognized', httpStatus: 404, message: 'errors.labor.pin_not_recognized', retryable: false,
  details: z.object({}),
});

// ---- events ----------------------------------------------------------------------------------------------------------
const timecard = { aggregate: 'timecard', deviceConsumed: true } as const;

export const ClockedIn = defineEvent({
  ...timecard,
  name: 'ClockedIn',
  version: 1,
  summary: "An employee clocked in at the location; the time is the deciding replica's clock.",
  payload: z.object({ timecardId: TimecardId, employeeId: EmployeeId, clockedInAt: EpochMs, businessDate: LocalDate, source: PunchSource }),
  verifiable: ['timecardId', 'employeeId', 'clockedInAt', 'businessDate'],
  merge: 'lifecycle',
});

export const BreakStarted = defineEvent({
  ...timecard,
  name: 'BreakStarted',
  version: 1,
  summary: 'A break began, with its definition as it stood (paid or unpaid, minutes, buffer).',
  payload: z.object({ breakId: BreakId, policy: BreakPolicySnapshot, startedAt: EpochMs }),
  verifiable: ['breakId', 'policy.paid', 'startedAt'],
  merge: 'lifecycle',
});

export const BreakEnded = defineEvent({
  ...timecard,
  name: 'BreakEnded',
  version: 2,
  summary: 'A break ended: by a punch, or with the clock-out that closed the card.',
  payload: z.object({
    breakId: BreakId,
    endedAt: EpochMs,
    /** v2 (lane D4, B85): the manager who approved ending the break before its minutes were up; absent otherwise. */
    approvedBy: EmployeeId.optional(),
  }),
  verifiable: ['breakId', 'endedAt'],
  merge: 'lifecycle',
  // v2 only added the optional approver: a v1 break-in (an older register's, Andrew's build 45 among them) reads as a v2
  // one with none, and a register that reads v1 gets the fact without it.
  upcastFrom: [additive(1)],
  downcastTo: [{ version: 1, down: (newer) => withoutApprover(newer) }],
});

/** A BreakEnded v2 wire payload as v1 (the same fields without `approvedBy`). */
function withoutApprover(newer: unknown): unknown {
  if (newer === null || typeof newer !== 'object') return newer;
  return Object.fromEntries(Object.entries(newer).filter(([key]) => key !== 'approvedBy'));
}

export const PaidBreakOverrunConverted = defineEvent({
  ...timecard,
  name: 'PaidBreakOverrunConverted',
  version: 1,
  summary: 'A paid break ran past its minutes and buffer; the overrun continues as an unpaid excess break.',
  payload: z.object({ breakId: BreakId, paidEndedAt: EpochMs, excessBreakId: BreakId, excessStartedAt: EpochMs }),
  verifiable: ['breakId', 'paidEndedAt', 'excessStartedAt'],
  merge: 'lifecycle',
});

export const ClockedOut = defineEvent({
  ...timecard,
  name: 'ClockedOut',
  version: 1,
  summary: 'The card closed: a clock-out, or the sweep at the business-day start (`auto`).',
  payload: z.object({ clockedOutAt: EpochMs, auto: z.boolean() }),
  verifiable: ['clockedOutAt', 'auto'],
  merge: 'lifecycle',
});

export const TimecardCreated = defineEvent({
  ...timecard,
  name: 'TimecardCreated',
  version: 2,
  summary: 'A manager entered a card for a shift that was never punched, with a reason.',
  payload: z.object({
    timecardId: TimecardId, employeeId: EmployeeId, card: CardTimes, reason: z.string().min(1).max(500), note: z.string().max(500).optional(),
    createdBy: ActorRef, createdAt: EpochMs,
  }),
  verifiable: ['employeeId', 'card'],
  merge: 'lifecycle',
  // v2 only added the optional `legacyRowPaysBuffer` on a break: a v1 card reads as a v2 one without it, and a reader on
  // v1 (an older register, which only folds the card) gets the card without it. An older node decides AdjustTimecard
  // from what it stores, so it is held at update_required instead (#2849 review B2).
  upcastFrom: [additive(1)],
  downcastTo: [{ version: 1, down: (newer) => withoutRowFlags(newer, ['card']), lossyForDeciders: true }],
});

export const TimecardAdjusted = defineEvent({
  ...timecard,
  name: 'TimecardAdjusted',
  version: 2,
  summary: "A manager corrected a card's times or breaks: what it was, what it became, why and who.",
  payload: z.object({
    before: CardTimes, after: CardTimes, reason: z.string().min(1).max(500), note: z.string().max(500).optional(), adjustedBy: ActorRef, adjustedAt: EpochMs,
  }),
  verifiable: ['before', 'after'],
  merge: 'lifecycle',
  // v2 as TimecardCreated's: `before` and `after` breaks may carry `legacyRowPaysBuffer`; a v1 register gets them without,
  // and a v1 node is held (it would clip the next lengthening adjust at the minutes, not minutes plus buffer).
  upcastFrom: [additive(1)],
  downcastTo: [{ version: 1, down: (newer) => withoutRowFlags(newer, ['before', 'after']), lossyForDeciders: true }],
});

/** A TimecardCreated or TimecardAdjusted v2 wire payload as v1: each named card's breaks without `legacyRowPaysBuffer`. */
function withoutRowFlags(newer: unknown, cards: readonly string[]): unknown {
  if (newer === null || typeof newer !== 'object') return newer;
  const out: Record<string, unknown> = { ...(newer as Record<string, unknown>) };
  for (const key of cards) {
    const card = out[key];
    if (card === null || typeof card !== 'object') continue;
    const breaks = (card as Record<string, unknown>)['breaks'];
    if (!Array.isArray(breaks)) continue;
    out[key] = { ...(card as Record<string, unknown>), breaks: breaks.map((b: unknown) => (b !== null && typeof b === 'object' ? Object.fromEntries(Object.entries(b).filter(([k]) => k !== 'legacyRowPaysBuffer')) : b)) };
  }
  return out;
}

export const TimecardDeleted = defineEvent({
  ...timecard,
  name: 'TimecardDeleted',
  version: 1,
  summary: 'A manager deleted a card, with a reason (legacy deleted silently).',
  payload: z.object({ reason: z.string().min(1).max(500), deletedBy: ActorRef, deletedAt: EpochMs }),
  verifiable: [],
  merge: 'lifecycle',
});

export const TimecardNoteAdded = defineEvent({
  ...timecard,
  name: 'TimecardNoteAdded',
  version: 2,
  summary: "A manager's note on one line of the card's edit log (legacy Add note), replacing that line's note.",
  payload: z.object({
    editIndex: z.int().nonnegative(),
    /**
     * v2 (lane SB, EP-JOB-04 round 8): the decision stamp (HLC) of the correction the note is on. The log keeps its
     * corrections in decision order, so a correction that lands late moves later lines down; the stamp finds the line
     * wherever it is. Absent on a note about a line with no stamp, and on every v1 note (which names the line by index).
     */
    editHlc: Hlc.optional(),
    note: z.string().min(1).max(500), notedBy: ActorRef, notedAt: EpochMs,
  }),
  verifiable: ['editIndex', 'note'],
  merge: 'lifecycle',
  // v2 only added the optional stamp: a v1 note reads as a v2 one without it, and a register that reads v1 gets the
  // note by index.
  upcastFrom: [additive(1)],
  downcastTo: [{ version: 1, down: (newer) => withoutEditHlc(newer) }],
});

/** A TimecardNoteAdded v2 wire payload as v1 (the same fields without `editHlc`). */
function withoutEditHlc(newer: unknown): unknown {
  if (newer === null || typeof newer !== 'object') return newer;
  return Object.fromEntries(Object.entries(newer).filter(([key]) => key !== 'editHlc'));
}

/** Every event the timecard aggregate records, in the order core's `TimecardEvent` union lists them. */
export const timecardEvents = [
  ClockedIn, BreakStarted, BreakEnded, PaidBreakOverrunConverted, ClockedOut, TimecardCreated, TimecardAdjusted, TimecardDeleted, TimecardNoteAdded,
] as const;

// ---- commands --------------------------------------------------------------------------------------------------------
const onCard = { stream: { aggregate: 'timecard', id: (input: { timecardId: string }) => input.timecardId } } as const;
const punch = { ...onCard, availability: 'location', origin: 'device', risk: 'low', dryRun: false, agent: { expose: false }, permission: { session: 'staff' }, output: TimecardSnapshot } as const;
const office = { ...onCard, availability: 'location', origin: 'any', permission: 'labor.timecards.manage', output: TimecardSnapshot } as const;
const punchErrors = [TimecardNotFound, TimecardRuleViolation] as const;
const editErrors = [TimecardNotFound, TimecardOverlap, SpansBusinessDay, TimecardRuleViolation] as const;

export const ClockIn = defineCommand({
  ...punch,
  name: 'labor.clockIn',
  summary: 'Clock an employee in at the location. Already clocked in: the open card is returned and nothing changes.',
  input: z.object({ timecardId: TimecardId, locationId: LocationId, employeeId: EmployeeId, source: PunchSource }),
  emits: [ClockedIn],
  errors: [EmployeeCannotClockIn, TimecardRuleViolation],
  legacy: ['GET /v1/employees/{employee_id}/clock-in'],
});

export const ClockOut = defineCommand({
  ...punch,
  name: 'labor.clockOut',
  summary: 'Clock out; an open break ends at the clock-out.',
  input: z.object({ timecardId: TimecardId }),
  emits: [PaidBreakOverrunConverted, BreakEnded, ClockedOut],
  errors: punchErrors,
  legacy: ['GET /v1/employees/{employee_id}/clock-out'],
});

export const StartBreak = defineCommand({
  ...punch,
  name: 'labor.startBreak',
  summary: 'Start a break of one of the location break definitions (never a second open break).',
  input: z.object({ timecardId: TimecardId, breakId: BreakId, breakPolicyId: BreakPolicyId }),
  emits: [BreakStarted],
  errors: punchErrors,
  legacy: ['POST /v1/employees/{employee_id}/break-out'],
});

export const EndBreak = defineCommand({
  ...punch,
  name: 'labor.endBreak',
  summary: 'End the open break.',
  input: z.object({ timecardId: TimecardId }),
  emits: [PaidBreakOverrunConverted, BreakEnded],
  errors: punchErrors,
  legacy: ['GET /v1/employees/{employee_id}/break-in'],
});

export const AutoClockOut = defineCommand({
  ...office,
  name: 'labor.autoClockOut',
  summary: "Close an open card at its business day's end (the sweep at the business-day start).",
  input: z.object({ timecardId: TimecardId }),
  risk: 'low',
  dryRun: false,
  agent: { expose: false },
  emits: [PaidBreakOverrunConverted, BreakEnded, ClockedOut],
  errors: punchErrors,
  legacy: ['maintenance:auto-clockout'],
});

export const ConvertPaidBreakOverrun = defineCommand({
  ...office,
  name: 'labor.convertPaidBreakOverrun',
  summary: 'End an open paid break that ran past its minutes and buffer, and continue it as an unpaid excess break (the overrun job; the excess break id is derived from the paid break id).',
  input: z.object({ timecardId: TimecardId }),
  risk: 'low',
  dryRun: false,
  agent: { expose: false },
  emits: [PaidBreakOverrunConverted],
  errors: punchErrors,
  legacy: ['PaidBreakOverTime (every minute)'],
});

export const CreateTimecard = defineCommand({
  ...office,
  name: 'labor.createTimecard',
  summary: 'Enter a card for a shift that was never punched, typed on the location clock, with a reason.',
  input: z.object({
    timecardId: TimecardId, locationId: LocationId, employeeId: EmployeeId, card: CardEdit, reason: z.string().min(1).max(500), note: z.string().max(500).optional(),
  }),
  risk: 'medium',
  dryRun: true,
  agent: { expose: false },
  emits: [TimecardCreated],
  errors: [EmployeeCannotClockIn, ...editErrors],
  legacy: ['POST /v1/timecards'],
});

export const AdjustTimecard = defineCommand({
  ...office,
  name: 'labor.adjustTimecard',
  summary: "Correct a card's clock times and breaks, typed on the location clock, with a reason.",
  input: z.object({ timecardId: TimecardId, card: CardEdit, reason: z.string().min(1).max(500), note: z.string().max(500).optional() }),
  risk: 'medium',
  dryRun: true,
  agent: { expose: false },
  emits: [TimecardAdjusted],
  errors: editErrors,
  legacy: ['PUT /v1/timecards/{id}'],
});

export const DeleteTimecard = defineCommand({
  ...office,
  name: 'labor.deleteTimecard',
  summary: 'Delete a card, with a reason; it stays in the history.',
  input: z.object({ timecardId: TimecardId, reason: z.string().min(1).max(500) }),
  risk: 'medium',
  dryRun: true,
  agent: { expose: false },
  emits: [TimecardDeleted],
  errors: punchErrors,
  legacy: ['DELETE /v1/timecards/{id}'],
});

export const AddTimecardNote = defineCommand({
  ...office,
  name: 'labor.addTimecardNote',
  summary: "Note one line of a card's edit log (its position in the card's history), replacing that line's note.",
  input: z.object({ timecardId: TimecardId, editIndex: z.int().nonnegative(), note: z.string().trim().min(1).max(500) }),
  risk: 'low',
  dryRun: false,
  agent: { expose: false },
  emits: [TimecardNoteAdded],
  errors: punchErrors,
  legacy: ['PUT /v1/timecard-events/{id}'],
});

// ---- queries ---------------------------------------------------------------------------------------------------------
export const GetTimecard = defineQuery({
  name: 'labor.getTimecard',
  summary: 'Read one card with its breaks and full edit history, every time on the location clock.',
  input: z.object({ timecardId: TimecardId }),
  output: TimecardSnapshot,
  permission: 'labor.timecards.manage',
  availability: 'location',
  scope: 'location',
  errors: [TimecardNotFound],
  agent: { expose: true, surfaces: ['backoffice.assistant', 'crew.manager'] },
  legacy: ['GET /v1/timecards/{id}'],
});

/** The confirmations a work period asks for (core `MealAdvisory`). */
export const MealAdvisory = z
  .enum(['confirm_bona_fide_meal_routine', 'confirm_bona_fide_meal_split_shift', 'tracking_incomplete_may_owe_one'])
  .meta({ id: 'MealAdvisory' });

/**
 * One meal's verdict (core `MealVerdict`, legacy `meals[]` entry): the deadline is when worked time reached 5 h (first)
 * or 10 h (second); `taken` the start of the meal or short break that settled it; `workday` the business day an owed
 * meal counts toward. `countedTowardSecond` and `waiverVoid` name why a clean miss is owed (legacy reason copy).
 */
export const MealVerdictView = z
  .object({
    meal: z.union([z.literal(1), z.literal(2)]),
    state: z.enum(['clear', 'waived', 'review', 'owed', 'owed_short', 'owed_late']),
    deadline: LocationTime,
    taken: LocationTime.nullable(),
    workday: LocalDate.nullable(),
    countedTowardSecond: z.boolean(),
    waiverVoid: z.boolean(),
  })
  .meta({ id: 'MealVerdictView' });

/**
 * One flagged business day of an employee's California meal premium on the timecards list (legacy `premium_days`, core
 * `assessMealPremiums` over cards 3 days either side of the range). `outsideRange` days were read only so a work period
 * crossing the range edge is judged whole: their cards are not listed and the day roll-up skips them, but a listed card
 * feeding one still carries its chip. `windowSaturated`: a card beyond even that window would have chained in.
 */
export const TimecardMealPremiumDay = z
  .object({
    employeeId: EmployeeId,
    businessDate: LocalDate,
    state: z.enum(['owed', 'owed_late', 'review']),
    /** One hour of base wage on an owed day; zero on a review day or with a wage under $7.25. */
    premium: Money,
    timecardIds: z.array(TimecardId),
    windowSaturated: z.boolean(),
    outsideRange: z.boolean(),
    /** Each meal whose requirement arose in the work period behind the day (legacy `meals[]`). */
    meals: z.array(MealVerdictView),
    /** Confirmations the period asks of a manager (legacy `advisory_flags`). */
    advisories: z.array(MealAdvisory),
    /** The business date each contributing card was worked on (legacy `contributing_timecard_days`). */
    cardDays: z.array(z.object({ timecardId: TimecardId, businessDate: LocalDate })),
    /** Owed, but the wage is missing or under $7.25: no amount can be computed (legacy's "no wage on file" reason). */
    invalidWage: z.boolean(),
  })
  .meta({ id: 'TimecardMealPremiumDay' });

/**
 * The marker a closed listed card carries (legacy `TimecardMealPremiumChip`, core `timecardMealChip`): can't evaluate (its
 * chain hit the window edge), causes a violation alone, part of a series that owes, or feeds only a day to review.
 * `businessDate` is the day it feeds (absent for a card that trips a violation alone).
 */
export const TimecardMealChip = z
  .object({
    timecardId: TimecardId,
    chip: z.enum(['cant_evaluate', 'causes_violation', 'part_of_series', 'review']),
    businessDate: LocalDate.optional(),
  })
  .meta({ id: 'TimecardMealChip' });

export const ListTimecards = defineQuery({
  name: 'labor.listTimecards',
  summary: "List a location's cards by business date (inclusive range), every time on the location clock.",
  input: z.object({ locationId: LocationId, from: LocalDate, to: LocalDate, employeeId: EmployeeId.optional() }),
  output: z.object({
    timecards: z.array(TimecardSummary),
    /** With "Flag potential meal-period premiums" on (`labor.meal_premium.flagging_enabled`): flagged days and card chips. Null when off. */
    mealPremium: z.object({ days: z.array(TimecardMealPremiumDay), chips: z.array(TimecardMealChip) }).nullable(),
  }),
  permission: 'labor.timecards.manage',
  availability: 'location',
  scope: 'location',
  errors: [],
  agent: { expose: true, surfaces: ['backoffice.assistant', 'crew.manager'] },
  legacy: ['GET /v2/timecards'],
});

/**
 * Reports › Meal premiums (legacy `GET /v1/reports/meal-premiums`, `MealPremiumReportService`; no legacy screen reads it,
 * the assistant does): per employee of the location (by status), the business days in the range with a premium owed or
 * to review, judged over cards 3 days either side as the payroll report is. An employee with nothing flagged in range is
 * left out. `totalPremium` is null when something is owed but the wage is under $7.25 (never $0.00), and null when only
 * days to review are flagged. Empty when the location does not flag meal-period premiums. Advisory only; labor-law
 * sign-off waits on asks A16.
 */
export const MealPremiumsReport = defineQuery({
  name: 'labor.mealPremiumsReport',
  summary: 'California meal-period premiums owed or to review per employee per business day for a range (an estimate on base wage).',
  input: z.object({ locationId: LocationId, from: LocalDate, to: LocalDate, status: z.enum(['active', 'inactive']).optional() }),
  output: z.object({
    /** The location flags meal-period premiums (`labor.meal_premium.flagging_enabled`). */
    enabled: z.boolean(),
    /** The sum of every listed employee's computable premium. */
    totalPremium: Money,
    employees: z.array(z.object({
      employeeId: EmployeeId,
      /** Business days in range with a premium owed (legacy `premium_workdays`). */
      owedDays: z.int().nonnegative(),
      totalPremium: Money.nullable(),
      days: z.array(TimecardMealPremiumDay.omit({ employeeId: true, outsideRange: true }).meta({ id: 'MealPremiumReportDay' })),
    })),
  }),
  // Legacy `backoffice.permission:view_reports_payroll` on the route.
  permission: 'reports.payroll.view',
  availability: 'location',
  scope: 'location',
  errors: [],
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  legacy: ['GET /v1/reports/meal-premiums'],
});

/**
 * Who a PIN belongs to at a location (the PIN pad before the time clock). Sent in a POST body, never a URL, and
 * never stored: PINs live only as hashes, and commands carry the employee id the PIN resolved to.
 */
export const IdentifyByPin = defineQuery({
  name: 'labor.identifyByPin',
  summary: "Resolve a staff PIN at a location to the employee and their open card, if any.",
  input: z.object({ locationId: LocationId, pin: secret(z.string().regex(/^\d{4,8}$/)) }),
  output: z.object({ employeeId: EmployeeId, employeeName: z.string(), openTimecard: TimecardSummary.nullable() }),
  permission: { session: 'device' },
  availability: 'location',
  scope: 'location',
  errors: [PinNotRecognized],
  agent: { expose: false },
});

/**
 * Reports › Breaks (legacy `GET /v1/reports/breaks`, field note backoffice.reports.management.breaks): every break
 * punched in a business-date range, per employee — Time-out, Time-in, Name, Type — with every employee of the location
 * listed. Excess break time is a typed row (legacy returned it nameless and typeless).
 */
export const BreaksReport = defineQuery({
  name: 'labor.breaksReport',
  // E-09: a back-office session also needs Payroll Reports (legacy checked it in the browser only).
  backofficeAlso: ['reports.payroll.view'],
  summary: 'Every break punched in a business-date range, per employee (time out, time in, break name, paid or unpaid).',
  input: z.object({ locationId: LocationId, from: LocalDate, to: LocalDate }),
  output: z.object({
    employees: z.array(z.object({
      employeeId: EmployeeId,
      breaks: z.array(z.object({
        timecardId: TimecardId, breakId: BreakId, name: z.string().nullable(), kind: BreakKind, out: LocationTime, in: LocationTime.nullable(),
      })),
    })),
  }),
  permission: 'reports.view',
  availability: 'location',
  scope: 'location',
  errors: [],
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  legacy: ['GET /v1/reports/breaks'],
});

// ---- labor admin: break definitions and staff PINs -------------------------------------------------------------------
/**
 * A location's break definition (legacy `employee_breaks`). A config entity (ADR-015, `labor.break_policy`): written at
 * the node or in the cloud and replicated field by field, so a node keeps deciding breaks with the definitions in force.
 * A break records the definition it was taken under, so editing or retiring one never changes history (T10 FIX).
 */
export const BreakPolicyFields = z
  .object({
    name: z.string().trim().min(1).max(255),
    minutes: z.int().min(1).max(600),
    bufferMinutes: z.int().min(0).max(600),
    paid: z.boolean(),
    convertOverrun: z.boolean(),
    payForBuffer: z.boolean(),
    /**
     * Excessive Break Tracking (legacy `excessive_paid_break_tracking`, on paid and unpaid definitions): with Convert
     * excess time to unpaid off, the timecard shows how far a break overstayed. Absent = off (written before it existed).
     */
    trackPaidOverstay: z.boolean().optional(),
  })
  .meta({ id: 'BreakPolicyFields' });

export const BreakPolicyView = BreakPolicyFields.extend({
  breakPolicyId: BreakPolicyId, locationId: LocationId, retired: z.boolean(), version: z.int().nonnegative(),
}).meta({ id: 'BreakPolicyView' });

export const BreakPolicyNotFound = defineError({
  code: 'labor.break_policy_not_found', httpStatus: 404, message: 'errors.labor.break_policy_not_found', retryable: false,
  details: z.object({ breakPolicyId: BreakPolicyId }),
});
/** A new definition must name every field; `field` is the first one missing. */
export const BreakPolicyIncomplete = defineError({
  code: 'labor.break_policy_incomplete', httpStatus: 422, message: 'errors.labor.break_policy_incomplete', retryable: false,
  details: z.object({ field: z.string() }),
});
export const PinTaken = defineError({
  code: 'labor.pin_taken', httpStatus: 409, message: 'errors.labor.pin_taken', retryable: false,
  details: z.object({}),
});
export const EmployeeNotFound = defineError({
  code: 'labor.employee_not_found', httpStatus: 404, message: 'errors.labor.employee_not_found', retryable: false,
  details: z.object({ employeeId: EmployeeId }),
});

const breakAdmin = {
  availability: 'location', origin: 'any', emits: [ConfigFieldsWritten], permission: 'settings.staff.manage', output: BreakPolicyView,
  risk: 'medium', dryRun: true, agent: { expose: false },
} as const;

export const UpsertBreakPolicy = defineCommand({
  ...breakAdmin,
  name: 'labor.upsertBreakPolicy',
  summary: 'Create a break definition at a location, or change some of its fields; a change of minutes or paid moves the upcoming shifts and templates that plan it.',
  input: z.object({ breakPolicyId: BreakPolicyId, locationId: LocationId, changes: BreakPolicyFields.partial() }),
  errors: [BreakPolicyIncomplete, BreakPolicyNotFound, CloudAuthoritative],
  legacy: ['POST /v1/employee-breaks', 'PUT /v1/employee-breaks/{id}'],
});

export const RetireBreakPolicy = defineCommand({
  ...breakAdmin,
  name: 'labor.retireBreakPolicy',
  summary: 'Retire a break definition: no new break can start under it; breaks already taken keep it.',
  input: z.object({
    breakPolicyId: BreakPolicyId,
    locationId: LocationId,
    /**
     * Also take it off the upcoming shifts that plan it (legacy `strip_upcoming_shifts`). Templates and the default
     * breaks always drop it; without this, upcoming shifts keep it, marked as retired (B1571).
     */
    stripUpcomingShifts: z.boolean().optional(),
  }),
  errors: [BreakPolicyNotFound, CloudAuthoritative],
  legacy: ['DELETE /v1/employee-breaks/{id}'],
});

/** Where a retired definition is taken off: one upcoming shift, one template, or the location's default breaks. */
const RetiredBreakTarget = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('shift'), shiftId: ShiftId }),
  z.object({ kind: z.literal('template'), templateId: ScheduleTemplateId }),
  z.object({ kind: z.literal('defaultBreaks') }),
]).meta({ id: 'RetiredBreakTarget' });

/**
 * Take a retired break definition off one shift, template or the default breaks (legacy SyncShiftBreaks
 * `stripDefinition`, `stripFromTemplates`, `stripFromDefaultBreaks`). Each is its own stream, so `labor.retireBreakPolicy`
 * brings one of these per target, recorded with it in one transaction. Refused while the definition is live, on a shift
 * that has started (it keeps the plan it started with), and where the target does not name it.
 */
export const DropRetiredBreak = defineCommand({
  availability: 'location', origin: 'any', emits: [ConfigFieldsWritten], permission: 'settings.staff.manage', risk: 'low', dryRun: false,
  agent: { expose: false },
  name: 'labor.dropRetiredBreak',
  summary: 'Take a retired break definition off an upcoming shift, a schedule template or the default breaks.',
  input: z.object({ breakPolicyId: BreakPolicyId, locationId: LocationId, target: RetiredBreakTarget }),
  output: z.object({ breakPolicyId: BreakPolicyId, locationId: LocationId }),
  errors: [BreakPolicyNotFound, CloudAuthoritative],
  legacy: ['DELETE /v1/employee-breaks/{id}'],
});

export const RecordBreakPolicyName = defineCommand({
  availability: 'location', origin: 'any', emits: [ConfigFieldsWritten], permission: 'settings.staff.manage', risk: 'low', dryRun: false,
  agent: { expose: false },
  name: 'labor.recordBreakPolicyName',
  summary: "Record a break definition's name where registers keep it after the definition is retired (B1623).",
  input: z.object({ breakPolicyId: BreakPolicyId, locationId: LocationId }),
  output: z.object({ breakPolicyId: BreakPolicyId, locationId: LocationId }),
  errors: [BreakPolicyNotFound, CloudAuthoritative],
  legacy: ['POST /v1/employee-breaks', 'PUT /v1/employee-breaks/{id}', 'DELETE /v1/employee-breaks/{id}'],
});

export const ListBreakPolicies = defineQuery({
  name: 'labor.listBreakPolicies',
  summary: "A location's break definitions, retired ones included.",
  input: z.object({ locationId: LocationId }),
  output: z.object({ breakPolicies: z.array(BreakPolicyView) }),
  // The Back Office reads it (Employee Breaks, Staff Tips, the timecard editor); registers read the synced config.
  permission: { session: 'user' },
  availability: 'location',
  scope: 'location',
  errors: [],
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  legacy: ['GET /v1/employee-breaks'],
});

/** An upcoming shift or a schedule template whose planned breaks follow a changed definition. */
const PlannedBreakTarget = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('shift'), shiftId: ShiftId }),
  z.object({ kind: z.literal('template'), templateId: ScheduleTemplateId }),
]).meta({ id: 'PlannedBreakTarget' });

/**
 * Bring one upcoming shift's or one template's planned breaks of a definition up to the definition's name, minutes and
 * paid (legacy SyncShiftBreaks `propagateDefinitionChange`). Each is its own stream, so `labor.upsertBreakPolicy` brings
 * one of these per target when minutes or paid change, recorded with it in one transaction. Refused for a retired
 * definition, on a shift that has started (it keeps the plan it started with), and where the target does not name it.
 */
export const RefreshPlannedBreak = defineCommand({
  availability: 'location', origin: 'any', emits: [ConfigFieldsWritten], permission: 'settings.staff.manage', risk: 'low', dryRun: false,
  agent: { expose: false },
  name: 'labor.refreshPlannedBreak',
  summary: "Bring an upcoming shift's or a schedule template's planned breaks up to their definition's minutes and paid.",
  input: z.object({ breakPolicyId: BreakPolicyId, locationId: LocationId, target: PlannedBreakTarget }),
  output: z.object({ breakPolicyId: BreakPolicyId, locationId: LocationId }),
  errors: [BreakPolicyNotFound, CloudAuthoritative],
  legacy: ['PUT /v1/employee-breaks/{id}'],
});

/**
 * Where a break definition is planned (legacy `GET /v1/employee-breaks/{id}/usage`, BreakDefinitionUsage): the warning a
 * manager sees before changing or deleting it. Counts only upcoming live shifts (one that has started keeps its plan),
 * published and draft apart; the template shifts that plan it; and whether the default breaks name it (B1571).
 */
export const BreakPolicyUsageView = z
  .object({
    upcomingShifts: z.int().nonnegative(),
    upcomingPublishedShifts: z.int().nonnegative(),
    upcomingDraftShifts: z.int().nonnegative(),
    templateShifts: z.int().nonnegative(),
    inDefaultBreaks: z.boolean(),
    nextShiftDate: LocalDate.nullable(),
  })
  .meta({ id: 'BreakPolicyUsageView' });

export const BreakPolicyUsage = defineQuery({
  name: 'labor.breakPolicyUsage',
  summary: 'Where a break definition is planned: upcoming shifts (published and draft), template shifts and the default breaks.',
  input: z.object({ breakPolicyId: BreakPolicyId, locationId: LocationId }),
  output: BreakPolicyUsageView,
  permission: 'settings.staff.manage',
  availability: 'location',
  scope: 'location',
  errors: [BreakPolicyNotFound],
  agent: { expose: false },
  legacy: ['GET /v1/employee-breaks/{id}/usage'],
});

/**
 * Set or change an employee's time-clock PIN at a location. Stored only as a salted hash and a keyed lookup value; a PIN
 * is unique at its location. Not an event: a PIN never enters the event log, sync or a device.
 */
export const SetStaffPin = defineCommand({
  availability: 'location',
  origin: 'any',
  name: 'labor.setPin',
  summary: "Set or change an employee's time-clock PIN at a location (unique there; stored only as a hash).",
  input: z.object({ employeeId: EmployeeId, locationId: LocationId, pin: secret(z.string().regex(/^\d{4,8}$/)) }),
  output: z.object({ employeeId: EmployeeId, locationId: LocationId, pinLength: z.int().min(4).max(8) }),
  permission: 'labor.timecards.manage',
  risk: 'high',
  dryRun: true,
  agent: { expose: false },
  emits: [],
  errors: [PinTaken, EmployeeNotFound],
  legacy: ['PUT /v1/employees/{id} (pin)'],
});

export const laborAdminCommands = [UpsertBreakPolicy, RetireBreakPolicy, DropRetiredBreak, RefreshPlannedBreak, RecordBreakPolicyName, SetStaffPin] as const;
export const laborAdminQueries = [ListBreakPolicies, BreakPolicyUsage] as const;
